import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiMail,
  FiMapPin,
  FiLink,
  FiCalendar,
  FiTrash2,
  FiRefreshCw,
  FiFolder,
  FiFileText,
  FiExternalLink,
  FiAlertTriangle,
  FiUsers,
} from "react-icons/fi";
import { FaWallet, FaGoogle, FaGithub } from "react-icons/fa";
import {
  useGetAdminUserByIdQuery,
  useDeleteAdminUserMutation,
  useRestoreAdminUserMutation,
  useSyncUserWalletActivitiesMutation,
} from "../../redux/slices/api/adminApiSlice";
import { Bars } from "react-loader-spinner";
import { toast } from "react-toastify";
import WalletDisplay from "../../components/common/WalletDisplay";

const AdminUserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [permanentDelete, setPermanentDelete] = useState(false);

  const { data, isLoading, error } = useGetAdminUserByIdQuery(id);
  const [deleteUser, { isLoading: isDeleting }] = useDeleteAdminUserMutation();
  const [restoreUser, { isLoading: isRestoring }] = useRestoreAdminUserMutation();
  const [syncWalletActivities, { isLoading: isSyncing }] = useSyncUserWalletActivitiesMutation();

  const handleSyncWalletActivities = async () => {
    try {
      const result = await syncWalletActivities(id).unwrap();
      if (result.synced) {
        const communityUpdates = result.results?.communityMemberships?.updated || 0;
        const fundUpdates = result.results?.fundContributions?.updated || 0;
        if (communityUpdates > 0 || fundUpdates > 0) {
          toast.success(`Synced ${communityUpdates} memberships, ${fundUpdates} contributions`);
        } else {
          toast.info(result.message || "No new records to sync");
        }
      } else {
        toast.info(result.message || "No wallets to sync");
      }
    } catch (err) {
      toast.error(err?.data?.message || "Failed to sync wallet activities");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUser({ id, permanent: permanentDelete }).unwrap();
      toast.success(
        permanentDelete
          ? "User permanently deleted"
          : "User deleted successfully"
      );
      navigate("/admin/users");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete user");
    }
  };

  const handleRestore = async () => {
    try {
      await restoreUser(id).unwrap();
      toast.success("User restored successfully");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to restore user");
    }
  };

  const getDisplayName = (user) => {
    if (user?.role === "organization") {
      return user.fundername || "N/A";
    }
    return `${user?.firstname || ""} ${user?.lastname || ""}`.trim() || "N/A";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAuthIcon = (provider) => {
    switch (provider) {
      case "google":
        return <FaGoogle className="text-red-500" />;
      case "github":
        return <FaGithub className="text-gray-800" />;
      case "orcid":
        return <span className="text-green-600 font-bold text-sm">iD</span>;
      default:
        return <FaWallet className="text-primary" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Bars height="60" width="60" color="#8538FF" ariaLabel="loading" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600 mb-4">Failed to load user details.</p>
        <button
          onClick={() => navigate("/admin/users")}
          className="px-4 py-2 bg-primary text-white rounded-lg"
        >
          Back to Users
        </button>
      </div>
    );
  }

  const { user, projects, results } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/admin/users")}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <FiArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-secondary">User Details</h1>
          <p className="text-gray-500">View and manage user information</p>
        </div>
        <div className="flex items-center gap-2">
          {user.deleted ? (
            <>
              <button
                onClick={handleRestore}
                disabled={isRestoring}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <FiRefreshCw size={16} />
                Restore
              </button>
              <button
                onClick={() => {
                  setPermanentDelete(true);
                  setShowDeleteModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <FiTrash2 size={16} />
                Delete Permanently
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                setPermanentDelete(false);
                setShowDeleteModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FiTrash2 size={16} />
              Delete User
            </button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      {user.deleted && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <FiAlertTriangle className="text-red-600" size={20} />
          <p className="text-red-700">
            This user has been deleted. They cannot access the platform.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-xl bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                {user.images?.profile ? (
                  <img
                    src={user.images.profile}
                    alt={getDisplayName(user)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-primary font-bold text-2xl">
                    {getDisplayName(user)[0]?.toUpperCase() || "?"}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold text-secondary">
                    {getDisplayName(user)}
                  </h2>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.role === "organization"
                        ? "bg-purple-100 text-purple-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {user.role}
                  </span>
                </div>

                {user.title_rank && (
                  <p className="text-gray-600 mt-1">{user.title_rank}</p>
                )}

                {user.biography && (
                  <p className="text-gray-600 mt-2 line-clamp-3">
                    {user.biography}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-500">
                  {user.email && (
                    <div className="flex items-center gap-1">
                      <FiMail size={14} />
                      <span>{user.email}</span>
                      {user.email_verified && (
                        <span className="text-green-600 text-xs">(verified)</span>
                      )}
                    </div>
                  )}
                  {(user.location || user.nationality) && (
                    <div className="flex items-center gap-1">
                      <FiMapPin size={14} />
                      <span>{user.location || user.nationality}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <FiCalendar size={14} />
                    <span>Joined {formatDate(user.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Account Info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-secondary mb-4">
                Account Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Auth Provider</span>
                  <div className="flex items-center gap-2">
                    {getAuthIcon(user.auth_provider)}
                    <span className="capitalize">
                      {user.auth_provider || "wallet"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Wallet Linked</span>
                  <span
                    className={
                      user.wallet_linked || user.public_address
                        ? "text-green-600"
                        : "text-gray-400"
                    }
                  >
                    {user.wallet_linked || user.public_address ? "Yes" : "No"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Email Verified</span>
                  <span
                    className={
                      user.email_verified ? "text-green-600" : "text-gray-400"
                    }
                  >
                    {user.email_verified ? "Yes" : "No"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Status</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.deleted
                        ? "bg-red-100 text-red-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {user.deleted ? "Deleted" : "Active"}
                  </span>
                </div>
              </div>
            </div>

            {/* Wallet Info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-secondary">
                  Wallet Address
                </h3>
                {user.public_address && (
                  <button
                    onClick={handleSyncWalletActivities}
                    disabled={isSyncing}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                    title="Sync historical activities made with this wallet (memberships, contributions)"
                  >
                    {isSyncing ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    ) : (
                      <FiUsers size={14} />
                    )}
                    {isSyncing ? "Syncing..." : "Sync Activities"}
                  </button>
                )}
              </div>
              {user.public_address ? (
                <div className="bg-gray-50 rounded-lg p-3 break-all font-mono text-sm text-gray-600">
                  <WalletDisplay
                    address={user.public_address}
                    fallback={user.public_address}
                  />
                </div>
              ) : (
                <p className="text-gray-400">No wallet connected</p>
              )}
            </div>
          </div>

          {/* Additional Info */}
          {(user.university_college ||
            user.skills?.length > 0 ||
            user.websites?.length > 0 ||
            user.linkedIn ||
            user.x) && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-secondary mb-4">
                Additional Information
              </h3>
              <div className="space-y-4">
                {user.university_college && (
                  <div>
                    <span className="text-gray-500 text-sm">Institution</span>
                    <p className="text-secondary">{user.university_college}</p>
                  </div>
                )}

                {user.skills?.length > 0 && (
                  <div>
                    <span className="text-gray-500 text-sm">Skills</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {user.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(user.linkedIn || user.x || user.websites?.length > 0) && (
                  <div>
                    <span className="text-gray-500 text-sm">Links</span>
                    <div className="flex flex-wrap gap-3 mt-1">
                      {user.linkedIn && (
                        <a
                          href={user.linkedIn}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <FiLink size={14} />
                          LinkedIn
                        </a>
                      )}
                      {user.x && (
                        <a
                          href={`https://x.com/${user.x}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <FiLink size={14} />@{user.x}
                        </a>
                      )}
                      {user.websites?.map((website, index) => (
                        <a
                          key={index}
                          href={website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <FiExternalLink size={14} />
                          Website
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-secondary mb-4">Activity</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FiFolder className="text-primary" size={18} />
                  <span className="text-gray-600">Projects</span>
                </div>
                <span className="font-semibold text-secondary">
                  {projects?.length || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FiFileText className="text-primary-green" size={18} />
                  <span className="text-gray-600">Results</span>
                </div>
                <span className="font-semibold text-secondary">
                  {results?.length || 0}
                </span>
              </div>
            </div>
          </div>

          {/* User's Projects */}
          {projects?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-secondary mb-4">Projects</h3>
              <div className="space-y-3">
                {projects.slice(0, 5).map((project) => (
                  <Link
                    key={project._id}
                    to={`/admin/projects/${project._id}`}
                    className="block p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <p className="font-medium text-secondary truncate">
                      {project.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          project.state === "Open Calls"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {project.state}
                      </span>
                      <span className="text-xs text-gray-400">
                        {project.category}
                      </span>
                    </div>
                  </Link>
                ))}
                {projects.length > 5 && (
                  <p className="text-sm text-gray-500 text-center">
                    +{projects.length - 5} more projects
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-secondary mb-4">Timestamps</h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-gray-500">Created</span>
                <p className="text-secondary">{formatDate(user.createdAt)}</p>
              </div>
              <div>
                <span className="text-gray-500">Last Updated</span>
                <p className="text-secondary">{formatDate(user.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <FiAlertTriangle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary">
                  {permanentDelete ? "Permanently Delete User?" : "Delete User?"}
                </h3>
                <p className="text-sm text-gray-500">{getDisplayName(user)}</p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              {permanentDelete
                ? "This action cannot be undone. The user and all their data will be permanently removed."
                : "The user will be soft-deleted and can be restored later."}
            </p>

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                {permanentDelete ? "Delete Permanently" : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserDetail;
